import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDepartmentsBudgetsReportContext } from './context/DepartmentsBudgetsReportContext';
import { DepartmentsBudgetsReportTableProvider } from './context/DepartmentsBudgetsReportTableContext';
import DepartmentsBudgetsReportTable from './DepartmentsBudgetsReportTable';
import EmailReportModal from './EmailReportModal';
import GenerateDepartmentsBudgetsReportCriteria from './GenerateDepartmentsBudgetsReportCriteria';

import Button from 'CommonComponents/Button';
import SectionTile from 'CommonComponents/SectionTile';
import { useTabsContext } from 'CommonComponents/Tabs';

import useApiRequest from 'Hooks/useApiRequest';
import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

const GenerateDepartmentsBudgetsReport = ({ fetchReportUrl }) => {
    const [criteriaCollapsed, setCriteriaCollapsed] = useState(false);
    const [loadingExport, setLoadingExport] = useState(false);
    const [loadingEmail, setLoadingEmail] = useState(false);

    const { criteria, exportReport, sendReportEmail } = useDepartmentsBudgetsReportContext();

    const { handleTabChange } = useTabsContext();

    const toast = useToast();

    const emailReportModal = useModal();

    const isFirstRender = useRef(true);

    const reportFetchRequest = useApiRequest({ url: fetchReportUrl });

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        fetchReportData(criteria);
    }, [criteria]);

    const fetchReportData = useCallback(
        (criteria) => {
            const { reset, get } = reportFetchRequest;

            reset();

            return get({ criteria });
        },
        [reportFetchRequest.reset, reportFetchRequest.get],
    );

    const handelExportReport = () => {
        setLoadingExport(true);
        exportReport()
            .then(() => {
                handleTabChange('departments-budgets-report---archive');
            })
            .finally(() => {
                setLoadingExport(false);
            });
    };

    const handleEmailReport = (email) => {
        setLoadingEmail(true);

        sendReportEmail(email)
            .then(() => {
                emailReportModal.hide();
                handleTabChange('departments-budgets-report---archive');

                toast.success($t('The report has been sent to the specified email address(es).'));
            })
            .catch(() => {
                setLoadingEmail(false);

                toast.error($t('Failed to send the report. Please try again later.'));
            });
    };

    return (
        <div className="tw-flex-auto tw-flex tw-flex-col tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary tw-space-y-7 tw-h-full">
            <div className="tw-flex-initial">
                <SectionTile
                    flat
                    header={$t('Include Reports')}
                    collapsed={criteriaCollapsed}
                    onToggle={() => setCriteriaCollapsed(!criteriaCollapsed)}
                    visibly
                >
                    <GenerateDepartmentsBudgetsReportCriteria />
                </SectionTile>
            </div>

            <DepartmentsBudgetsReportTableProvider
                criteria={criteria}
                report={reportFetchRequest.data?.data}
                markupCategories={reportFetchRequest.data?.markupCategories}
                budgets={reportFetchRequest.data?.budgets}
                loading={reportFetchRequest.loading}
            >
                <DepartmentsBudgetsReportTable
                    actions={({ data }) => (
                        <div className="tw-ml-auto tw-flex tw-gap-x-2">
                            <Button onClick={handelExportReport} disabled={!data || loadingExport} loading={loadingExport}>
                                {$t('Export Report')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    emailReportModal.show();
                                }}
                                disabled={!data || loadingExport || loadingEmail}
                                loading={loadingEmail}
                            >
                                {$t('Email Report')}
                            </Button>
                        </div>
                    )}
                />
            </DepartmentsBudgetsReportTableProvider>

            <EmailReportModal modal={emailReportModal} onSend={handleEmailReport} loading={loadingEmail} />
        </div>
    );
};

export default GenerateDepartmentsBudgetsReport;
